import React, { useState } from "react";
import Header from "../Header";
import Sidebar from "../Sidebar";
import { Link, useLocation, useNavigate } from "react-router-dom";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import CreatableSelect from "react-select/creatable";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";

import { putOrgFeature } from "../../connections/putOrgFeature";
import { getOrgFeatureCategories } from "../../connections/getOrgFeatureCategories";
import { postOrgFeatureCategory } from "../../connections/postOrgFeatureCategory";

const UpdateOrgFeature = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const OrgFeatureData = location.state || null;

  const {
    user: { user },
  } = useSelector((store) => store);

  const [formState, setFormState] = useState({
    key: OrgFeatureData?.key || "",
    name: OrgFeatureData?.name || "",
    active: OrgFeatureData?.active ?? true,
    category: OrgFeatureData?.category
      ? {
          value: OrgFeatureData.category,
          label: OrgFeatureData.category,
          id: OrgFeatureData.organization_category_id,
        }
      : null,
    header_height: OrgFeatureData?.data?.header_height || "",
    footer_height: OrgFeatureData?.data?.footer_height || "",
    header_content: OrgFeatureData?.data?.header_content || "",
    footer_content: OrgFeatureData?.data?.footer_content || "",
    review_duration:OrgFeatureData?.data?.review_duration || "",
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["getOrgFeatureCategories"],
    queryFn: () => getOrgFeatureCategories(user?.token),
    enabled: !!user?.token,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const categoryOptions =
    categoriesData?.payload?.categories?.map((cat) => ({
      value: cat.name,
      label: cat.name,
      id: cat.id,
    })) || [];

  const handleChange = (name, value) => {
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formState?.key) {
      toast.error("Key is required");
      return;
    }
    if (!formState?.name) {
      toast.error("Name is required");
      return;
    }
    if (!formState?.category?.value) {
      toast.error("Category is required");
      return;
    }

    const categoryName = formState.category.value;
    let organization_category_id = formState.category.id;

    if (formState.category.__isNew__) {
      const catResult = await postOrgFeatureCategory(user?.token, categoryName);
      if (!catResult) return;
      organization_category_id = catResult?.payload?.organization_category?.id;
    }

    const payload = {
      feature: {
        key: formState.key,
        name: formState.name,
        active: formState.active,
        category: categoryName,
        organization_category_id,
        data: {
          header_height: formState.header_height,
          footer_height: formState.footer_height,
          header_content: formState.header_content,
          footer_content: formState.footer_content,
          review_duration: formState?.review_duration,
        },
      },
    };

    try {
      const result = await putOrgFeature(
        user?.token,
        payload,
        OrgFeatureData?.id,
      );
      if (result?.status?.status === "SUCCESS") {
        toast.success("Organization Feature Updated Successfully");
        navigate("/admin/organization_feature");
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  return (
    <div>
      <Header />
      <Sidebar activeClassName="origination--list" />
      <>
        <div className="page-wrapper">
          <div className="content">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-12">
                  <ul className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to={-1}>Organization Feature </Link>
                    </li>
                    <li className="breadcrumb-item">
                      <i className="feather-chevron-right">
                        <FeatherIcon icon="chevron-right" />
                      </i>
                    </li>
                    <li className="breadcrumb-item active">
                      Update Organization Feature
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    <form>
                      <div className="row">
                        <div className="col-12">
                          <div className="form-heading">
                            <h4>Update Organization Feature</h4>
                          </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-6">
                          <div className="form-group local-forms">
                            <label>
                              Key <span className="login-danger">*</span>
                            </label>
                            <input
                              value={formState?.key || ""}
                              name="key"
                              placeholder="key"
                              onChange={(e) =>
                                handleChange(
                                  "key",
                                  e.target.value.toLowerCase(),
                                )
                              }
                              className="form-control"
                              type="text"
                              required
                            />
                          </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-6">
                          <div className="form-group local-forms">
                            <label>
                              Name <span className="login-danger">*</span>
                            </label>
                            <input
                              value={formState?.name || ""}
                              name="name"
                              placeholder="name"
                              onChange={(e) =>
                                handleChange("name", e.target.value)
                              }
                              className="form-control"
                              type="text"
                              required
                            />
                          </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-6">
                          <div className="form-group local-forms">
                            <label>Category</label>
                            <CreatableSelect
                              options={categoryOptions}
                              value={formState?.category || null}
                              onChange={(option) =>
                                handleChange("category", option)
                              }
                              placeholder="Select or create category"
                              isClearable
                              menuPortalTarget={document.body}
                              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                          </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-6">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              fontSize: "12px",
                              fontWeight: 500,
                            }}
                          >
                            <label>Is Active</label>
                            <input
                              value={formState?.active || false}
                              checked={formState?.active}
                              name="active"
                              disabled
                              type="checkbox"
                              style={{ cursor: "not-allowed" }}
                            />
                          </div>
                        </div>

                        <div className="col-12">
                          <div className="form-heading">
                            <h4>Data</h4>
                          </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-3">
                          <div className="form-group local-forms">
                            <label style={{zIndex:1}}>Header Height</label>
                            <input
                              value={formState?.header_height || ""}
                              onChange={(e) =>
                                handleChange("header_height", e.target.value)
                              }
                              className="form-control"
                              type="number"
                              placeholder="5 in inch"
                            />
                          </div>
                        </div>

                        <div className="col-12 col-md-6 col-xl-3">
                          <div className="form-group local-forms">
                            <label style={{zIndex:1}}>Footer Height</label>
                            <input
                              value={formState?.footer_height || ""}
                              onChange={(e) =>
                                handleChange("footer_height", e.target.value)
                              }
                              className="form-control"
                              type="number"
                              placeholder="3 in inch"
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-6 col-xl-6">
                          <div className="form-group local-forms">
                            <label>
                              Review duration
                              <span className="login-danger">*</span>
                            </label>
                            <input
                              value={formState?.review_duration}
                              name="review_duration"
                              onChange={(e) =>
                                handleChange("review_duration", e.target.value)
                              }
                              className="form-control"
                              type="text"
                              required={true}
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="form-group local-forms">
                            <label>Header Content (HTML)</label>
                            <textarea
                              value={formState?.header_content || ""}
                              onChange={(e) =>
                                handleChange("header_content", e.target.value)
                              }
                              className="form-control"
                              rows={4}
                              placeholder="Your header content here"
                            />
                          </div>
                        </div>

                        <div className="col-12 col-md-6">
                          <div className="form-group local-forms">
                            <label>Footer Content (HTML)</label>
                            <textarea
                              value={formState?.footer_content || ""}
                              onChange={(e) =>
                                handleChange("footer_content", e.target.value)
                              }
                              className="form-control"
                              rows={4}
                              placeholder="Your footer content here"
                            />
                          </div>
                        </div>

                        {(formState?.header_content ||
                          formState?.footer_content) && (
                          <div className="col-12">
                            <div className="form-group local-forms">
                              <label>Preview</label>
                              <div
                                style={{
                                  border: "1px solid #dee2e6",
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                  fontFamily: "'Lato', sans-serif",
                                  background: "#fff",
                                }}
                              >
                                {formState?.header_content && (
                                  <div
                                    style={{
                                      minHeight: `${formState?.header_height || 50}mm`,
                                      padding: "16px 24px",
                                      borderBottom: "2px solid #333",
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: formState.header_content,
                                    }}
                                  />
                                )}
                                <div
                                  style={{
                                    padding: "24px",
                                    minHeight: "80px",
                                    color: "#aaa",
                                    fontSize: "12px",
                                    textAlign: "center",
                                  }}
                                >
                                  — prescription content area —
                                </div>
                                {formState?.footer_content && (
                                  <div
                                    style={{
                                      minHeight: `${formState?.footer_height || 30}mm`,
                                      padding: "12px 24px",
                                      borderTop: "2px solid #333",
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: formState.footer_content,
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="col-12">
                          <div className="doctor-submit text-end">
                            <button
                              onClick={handleSubmit}
                              type="submit"
                              className="btn btn-primary submit-form me-2"
                            >
                              Submit
                            </button>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                navigate("/admin/organization_feature");
                              }}
                              type="button"
                              className="btn btn-primary cancel-form"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

export default UpdateOrgFeature;
